import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  JobHeader, 
  JobContent, 
  JobSectionHeading, 
  UnifiedFooter,
  CTAButton
} from '../components';
import { Nav } from '../components/Nav';
import '../styles/global.css';

interface JobDescriptionProps {
  title: string;
  location: string;
  children: React.ReactNode;
}

const JobDescription: React.FC<JobDescriptionProps> = ({ title, location, children }) => {
  const [hovered, setHovered] = useState<string | null>(null);

  // Reset scroll position on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Set SEO title and meta
  useEffect(() => {
    document.title = `${title} | Careers at Syntra – Detect social engineering, verify requests`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Syntra helps IT helpdesks protect against social engineering and deepfakes with detection and verification tools..');
    }
  }, [title]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  // Prevent flash of unstyled content
  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    document.body.style.color = '#000000';
    document.body.style.margin = '0';
    
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.margin = '';
    };
  }, []);

  return (
    <motion.div 
      className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      variants={containerVariants}
    >
      {/* Navigation */}
      <Nav activeLink="careers" />

      {/* Job Header */}
      <JobHeader title={title} location={location} />
      
      {/* Job Content */}
      <JobContent>
        {children}
      </JobContent>

      {/* Footer */}
      <UnifiedFooter itemVariants={itemVariants} />
    </motion.div>
  );
};

// Individual job pages
export const ResearchEngineerPage = () => (
  <JobDescription title="Research Engineer" location="New York">
    <JobSectionHeading>Role</JobSectionHeading>
    <p>Research engineers at Syntra help build the future of human verification - discovering and designing defenses that ensure the person on the other end is who they say they are. You'll explore vulnerabilities and attacks, design detection systems that work at scale, and stay ahead of adversaries in a rapidly evolving space.</p>
    
    <JobSectionHeading>You might work on…</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li>Discovering new ways attackers can impersonate humans in IT and other critical workflows</li>
      <li>Designing and training deepfake and synthetic identity detectors that work in real-world conditions</li>
      <li>Building frameworks to red-team and stress-test human verification systems before attackers do</li>
      <li>Architecting detection pipelines that are both fast and reliable for high-stakes environments</li>
    </ul>
    
    <JobSectionHeading>You might be a fit if...</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li>You're excited by getting to - and staying at - the edge of an emerging field</li>
      <li>You've competed in security CTFs, love the thrill of finding exploits or read research papers for fun</li>
      <li>You're interested in diving deep into ML, audio signal processing or telephony protocols</li>
      <li>You care about turning messy research code into production-ready defenses</li>
    </ul>
    
    <JobSectionHeading>About</JobSectionHeading>
    <p>At Syntra, we're building the security and verification layer for human workflows—starting with IT, where deepfakes and account takeovers are most acute. Our goal is to make human verification seamless and reliable wherever it matters most, from IT to other critical domains.</p>
    
    <p>Our founders are former founding engineers at Vanta and developer platform and infrastructure leads at Dropbox. We work with cutting-edge voice AI companies and enterprises deploying agents at scale. We've raised $4.9M from top investors and leaders from security and AI communities. We work from a beautiful office in NYC's financial district, with a hybrid culture that balances deep work with in-person collaboration.</p>
    
    <p>We offer meaningful equity, premium health/dental/vision insurance, flexible PTO, and regular team events.</p>
    
    <JobSectionHeading>Operating principles</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li><strong>Move with conviction:</strong> Figure out what data you need, then bias to action.</li>
      <li><strong>Debug your own thinking:</strong> Be intentional and clear about your reasoning. Frameworks matter.</li>
      <li><strong>Spend innovation tokens wisely:</strong> Don't reinvent wheels. Focus energy where it matters most.</li>
      <li><strong>Operate with integrity:</strong> Trust is everything. Do the right thing for customers and partners.</li>
    </ul>
    
    <p>Our customers are the builders of the future. They're increasingly diverse, and we won't be able to support them well unless our team reflects that diversity. Whether you're new to security, have taken an unconventional career path, or come from an underrepresented background, we want to hear from you.</p>
    
    <JobSectionHeading>Applying</JobSectionHeading>
    <p>To apply, email careers@syntra.io with your resume. Optionally include any security, AI, or audio areas you've explored that you're most excited by. If there's a fit, we'll meet you very quickly.</p>
  </JobDescription>
);

export const ProductEngineerPage = () => (
  <JobDescription title="Product Engineer" location="New York">
    <JobSectionHeading>Role</JobSectionHeading>
    <p>Product engineers at Syntra build products that make human verification seamless and usable in the real world. You'll take complex research and package it into tools that help IT and security teams verify people in critical workflows.</p>
    
    <JobSectionHeading>You might work on…</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li>Designing APIs and tools that make human verification easy to integrate into IT and other workflows</li>
      <li>Building dashboards that help security and IT teams quickly spot and respond to suspicious activity</li>
      <li>Creating testing and simulation tools for red-teaming human verification systems</li>
      <li>Shipping features that help customers protect against deepfakes, social engineering, and identity attacks</li>
    </ul>
    
    <JobSectionHeading>You might be a fit if…</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li>You've taken a product from 0 to 1 - or want to</li>
      <li>You can translate between ML researchers and customers who just need their agents to be safe</li>
      <li>You're energized by customer conversations and love turning feedback into features</li>
    </ul>
    
    <JobSectionHeading>About</JobSectionHeading>
    <p>At Syntra, we're building the security and verification layer for human workflows—starting with IT, where deepfakes and account takeovers are most acute. Our goal is to make human verification seamless and reliable wherever it matters most, from IT to other critical domains.</p>
    
    <p>Our founders are former founding engineers at Vanta and developer platform and infrastructure leads at Dropbox. We work with cutting-edge voice AI companies and enterprises deploying agents at scale. We've raised $4.9M from top investors and leaders from security and AI communities. We work from a beautiful office in NYC's financial district, with a hybrid culture that balances deep work with in-person collaboration.</p>
    
    <p>We offer meaningful equity, premium health/dental/vision insurance, flexible PTO, and regular team events.</p>
    
    <JobSectionHeading>Operating principles</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li><strong>Move with conviction:</strong> Figure out what data you need, then bias to action.</li>
      <li><strong>Debug your own thinking:</strong> Be intentional and clear about your reasoning. Frameworks matter.</li>
      <li><strong>Spend innovation tokens wisely:</strong> Don't reinvent wheels. Focus energy where it matters most.</li>
      <li><strong>Operate with integrity:</strong> Trust is everything. Do the right thing for customers and partners.</li>
    </ul>
    
    <p>Our customers are the builders of the future. They're increasingly diverse, and we won't be able to support them well unless our team reflects that diversity. Whether you're new to security, have taken an unconventional career path, or come from an underrepresented background, we want to hear from you.</p>
    
    <JobSectionHeading>Applying</JobSectionHeading>
    <p>To apply, email careers@syntra.io with your resume. Optionally include anything you've built you're most excited by. If there's a fit, we'll meet you very quickly.</p>
  </JobDescription>
);

export const AnythingElsePage = () => (
  <JobDescription title="Anything else" location="New York">
    <JobSectionHeading>Role</JobSectionHeading>
    <p>We're building something new in a rapidly evolving space. If you're exceptional at what you do and think you can help us secure the future of voice AI, we want to hear from you - even if your expertise doesn't fit neatly into our other roles.</p>
    
    <JobSectionHeading>About</JobSectionHeading>
    <p>At Syntra, we're building the security and verification layer for IT and voice communications. We believe that voice and AI are transforming enterprises, but are vulnerable to attacks that traditional security tools miss. We're creating the infrastructure to detect and block these threats in real-time.</p>
    
    <p>Our founders are former founding engineers at Vanta and developer platform and infrastructure leads at Dropbox. We work with cutting-edge voice AI companies and enterprises deploying agents at scale. We've raised $4.9M from top investors and leaders from security and AI communities. We work from a beautiful office in NYC's financial district, with a hybrid culture that balances deep work with in-person collaboration.</p>
    
    <p>We offer meaningful equity, premium health/dental/vision insurance, flexible PTO, and regular team events.</p>
    
    <JobSectionHeading>Operating principles</JobSectionHeading>
    <ul className="list-disc pl-5 space-y-2 mb-6">
      <li><strong>Move with conviction:</strong> Figure out what data you need, then bias to action.</li>
      <li><strong>Debug your own thinking:</strong> Be intentional and clear about your reasoning. Frameworks matter.</li>
      <li><strong>Spend innovation tokens wisely:</strong> Don't reinvent wheels. Focus energy where it matters most.</li>
      <li><strong>Operate with integrity:</strong> Trust is everything. Do the right thing for customers and partners.</li>
    </ul>
    
    <p>Our customers are the builders of the future. They're increasingly diverse, and we won't be able to support them well unless our team reflects that diversity. Whether you're new to security, have taken an unconventional career path, or come from an underrepresented background, we want to hear from you.</p>
    
    <JobSectionHeading>Applying</JobSectionHeading>
    <p>To apply, email careers@syntra.io with your resume and a brief note explaining how you could help us achieve our mission. If there's a fit, we'll meet you very quickly.</p>
  </JobDescription>
);

// For routing
export const ResearchEngineer = ResearchEngineerPage;
export const ProductEngineer = ProductEngineerPage;
export const AnythingElse = AnythingElsePage;

export default JobDescription;
